import { Toaster } from "@/components/ui/sonner";
import { InstallBanner } from "@/components/InstallBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ArbiterOnlyRoute } from "./components/ArbiterOnlyRoute";
import { GreffeOnlyRoute } from "./components/GreffeOnlyRoute";
import GreffeFile from "./pages/greffe/GreffeFile";
import RecevabilitePage from "./pages/greffe/RecevabilitePage";
import TribunalPage from "./pages/greffe/TribunalPage";
import EnrolementPage from "./pages/greffe/EnrolementPage";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Audience from "./pages/Audience";
import CaseDecisions from "./pages/CaseDecisions";
import CaseDetailPage from "./pages/CaseDetail";
import Cockpit from "./pages/Cockpit";
import Confidentialite from "./pages/Confidentialite";
import Corpus from "./pages/Corpus";
import Dashboard from "./pages/Dashboard";
import DashboardAvocat from "./pages/DashboardAvocat";
import DeliberationWorkspace from "./pages/DeliberationWorkspace";
import DpoConformite from "./pages/DpoConformite";
import Formation from "./pages/Formation";
import Home from "./pages/Home";
import MentionsLegales from "./pages/MentionsLegales";
import NouveauDossier from "./pages/NouveauDossier";
import PortailClient from "./pages/PortailClient";
import Profil from "./pages/Profil";
import Recherche from "./pages/Recherche";
import RechercheAvocat from "./pages/RechercheAvocat";
import SentenceDraft from "./pages/SentenceDraft";

function Router() {
  return (
    <Switch>
      {/* Routes publiques (vitrine) */}
      <Route path="/" component={Home} />
      <Route path="/mentions-legales" component={MentionsLegales} />
      <Route path="/confidentialite" component={Confidentialite} />
      {/* Portail client PUBLIC : le client n'est pas un utilisateur plateforme,
          la page porte sa propre sécurité (OTP + session cloisonnée au dossier). */}
      <Route path="/portail/:token" component={PortailClient} />

      {/* Routes protegees (espace avocat / arbitre) */}
      <Route path="/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>
      {/* Espace Greffe (role GREFFIER / ADMIN) — recevabilite, affectation du
          tribunal. Server-driven : pilote par phase_procedurale. */}
      <Route path="/greffe">
        <ProtectedRoute>
          <GreffeOnlyRoute>
            <GreffeFile />
          </GreffeOnlyRoute>
        </ProtectedRoute>
      </Route>
      <Route path="/greffe/cases/:id/recevabilite">
        <ProtectedRoute>
          <GreffeOnlyRoute>
            <RecevabilitePage />
          </GreffeOnlyRoute>
        </ProtectedRoute>
      </Route>
      <Route path="/greffe/cases/:id/parties">
        <ProtectedRoute>
          <GreffeOnlyRoute>
            <EnrolementPage />
          </GreffeOnlyRoute>
        </ProtectedRoute>
      </Route>
      <Route path="/greffe/cases/:id/tribunal">
        <ProtectedRoute>
          <GreffeOnlyRoute>
            <TribunalPage />
          </GreffeOnlyRoute>
        </ProtectedRoute>
      </Route>
      {/* Cockpit Avocat (role PARTIE) — espace conseil, lecture seule des
          decisions, aucune transition d'etat (cf. masterplan Cockpit Avocat). */}
      <Route path="/avocat">
        <ProtectedRoute>
          <DashboardAvocat />
        </ProtectedRoute>
      </Route>
      <Route path="/avocat/nouveau-dossier">
        <ProtectedRoute>
          <NouveauDossier />
        </ProtectedRoute>
      </Route>
      {/* Recherche juridique côté avocat — corpus PUBLIC ; visible seulement
          si le flag VITE_RECHERCHE_AVOCAT_ENABLED est actif (garde dans la page). */}
      <Route path="/avocat/recherche">
        <ProtectedRoute>
          <RechercheAvocat />
        </ProtectedRoute>
      </Route>
      {/* Parcours de formation certifiante (consomme /api/v1/formation/*). */}
      <Route path="/formation">
        <ProtectedRoute>
          <Formation parcours="AVOCAT" />
        </ProtectedRoute>
      </Route>
      <Route path="/formation-arbitre">
        <ProtectedRoute>
          <Formation parcours="ARBITRE" />
        </ProtectedRoute>
      </Route>
      <Route path="/cockpit">
        <ProtectedRoute>
          <Cockpit />
        </ProtectedRoute>
      </Route>
      {/* Supervision conformite DPO. L'acces est garde cote serveur
          (est_dpo / ADMIN -> 403 sinon) ; la page affiche alors un message. */}
      <Route path="/dpo">
        <ProtectedRoute>
          <DpoConformite />
        </ProtectedRoute>
      </Route>
      <Route path="/cases/:caseId/decisions">
        <ProtectedRoute>
          <CaseDecisions />
        </ProtectedRoute>
      </Route>
      {/* Coffre-fort du Delibere : SECRET du delibere, exclusivement reserve
          aux arbitres (greffier du dossier toleree cote serveur). La PARTIE
          (avocat) n'y a jamais acces — le delibere n'est pas la decision : la
          sentence, elle, est consultable via « Decisions ». Garde UX +
          barriere serveur (arbitre/greffier du dossier -> 403 sinon). */}
      <Route path="/cases/:caseId/deliberation">
        <ProtectedRoute>
          <ArbiterOnlyRoute>
            <DeliberationWorkspace />
          </ArbiterOnlyRoute>
        </ProtectedRoute>
      </Route>
      {/* Redaction de sentence : outil juridictionnel reserve a l'arbitre.
          Le role PARTIE (avocat) est bloque par ArbiterOnlyRoute (separation
          des prerogatives Art. 20 AUA ; cf. masterplan Cockpit Avocat). */}
      <Route path="/cases/:caseId/sentence">
        <ProtectedRoute>
          <ArbiterOnlyRoute>
            <SentenceDraft />
          </ArbiterOnlyRoute>
        </ProtectedRoute>
      </Route>
      {/* Cockpit dossier = vue juridictionnelle (ArbiterShell). Réservé à
          l'arbitre/greffe : la PARTIE (avocat) reste dans /avocat — étanchéité
          stricte des rôles (la barrière serveur renvoie 403 de toute façon). */}
      <Route path="/cases/:caseId">
        <ProtectedRoute>
          <ArbiterOnlyRoute>
            <CaseDetailPage />
          </ArbiterOnlyRoute>
        </ProtectedRoute>
      </Route>
      <Route path="/audiences/:caseId">
        <ProtectedRoute>
          <Audience />
        </ProtectedRoute>
      </Route>
      <Route path="/profil">
        <ProtectedRoute>
          <Profil />
        </ProtectedRoute>
      </Route>
      <Route path="/recherche">
        <ProtectedRoute>
          <Recherche />
        </ProtectedRoute>
      </Route>
      <Route path="/corpus/:id">
        <ProtectedRoute>
          <Corpus />
        </ProtectedRoute>
      </Route>

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
            {/* Invite a installer en PWA sur iPad Safari (silver bullet
                ITP). Couche 3/3 de la PR auth perenne. */}
            <InstallBanner />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
